import { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

const reactPlugin = new ReactPlugin();

// Application Insights connection string from environment variable
// Falls back to empty string for local development without tracking
const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING || '';

const appInsights = new ApplicationInsights({
  config: {
    connectionString,
    extensions: [reactPlugin],
    enableAutoRouteTracking: true,
    enableCorsCorrelation: true,
    // Request headers can contain Authorization / cookies; don't ship them to AI.
    enableRequestHeaderTracking: false,
    enableResponseHeaderTracking: false,
    autoTrackPageVisitTime: true,
    disableFetchTracking: false,
    disableAjaxTracking: false,
    enableUnhandledPromiseRejectionTracking: true,
  },
});

// PII scrubbing telemetry initializer — runs on every outbound telemetry item.
const EMAIL_RE  = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const JWT_RE    = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const BEARER_RE = /Bearer\s+[A-Za-z0-9._~+/=-]+/gi;
const LONGNUM   = /\b\d{10,}\b/g;
const TOKEN_QS  = /([?&](?:access_token|id_token|token|code)=)[^&#]+/gi;

function scrubString(s: string): string {
  return s
    .replace(JWT_RE,    '<redacted:jwt>')
    .replace(BEARER_RE, 'Bearer <redacted>')
    .replace(TOKEN_QS,  '$1<redacted>')
    .replace(EMAIL_RE,  '<redacted:email>')
    .replace(LONGNUM,   '<redacted:number>');
}

function scrubRecord(obj: Record<string, unknown> | undefined): void {
  if (!obj) return;
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (typeof v === 'string') obj[k] = scrubString(v);
  }
}

function piiScrubber(item: ITelemetryItem): boolean {
  try {
    if (item?.baseData) {
      const bd = item.baseData as Record<string, unknown>;
      if (typeof bd.message === 'string') bd.message = scrubString(bd.message);
      if (typeof bd.name    === 'string') bd.name    = scrubString(bd.name);
      if (typeof bd.uri     === 'string') bd.uri     = scrubString(bd.uri);
      if (typeof bd.data    === 'string') bd.data    = scrubString(bd.data);
      scrubRecord(bd.properties as Record<string, unknown> | undefined);
    }
    // Remove username if it happened to slip in (auth context).
    if (item?.ext?.user) {
      (item.ext.user as Record<string, unknown>).authId = undefined;
    }
  } catch {
    /* never throw from a telemetry initializer */
  }
  return true;
}

if (connectionString) {
  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer(piiScrubber);
  appInsights.trackPageView();
}

export { reactPlugin, appInsights };
